import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { marketingApi } from '../lib/api';
import { toast } from 'sonner';

// Query Keys
export const marketingKeys = {
  all: ['marketing'],
  templates: () => [...marketingKeys.all, 'templates'],
  template: (id) => [...marketingKeys.templates(), id],
  campaigns: () => [...marketingKeys.all, 'campaigns'],
  campaign: (id) => [...marketingKeys.campaigns(), id],
  campaignRecipients: (id) => [...marketingKeys.campaign(id), 'recipients'],
  segments: () => [...marketingKeys.all, 'segments'],
};

// Templates Queries
export function useTemplates(params = {}) {
  return useQuery({
    queryKey: [...marketingKeys.templates(), params],
    queryFn: async () => {
      const res = await marketingApi.getTemplates(params);
      return res.data.templates || res.data || [];
    },
  });
}

export function useTemplate(id) {
  return useQuery({
    queryKey: marketingKeys.template(id),
    queryFn: async () => {
      const res = await marketingApi.getTemplate(id);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => marketingApi.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.templates() });
      toast.success('Template created successfully');
    },
    onError: () => {
      toast.error('Failed to create template');
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => marketingApi.updateTemplate(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.template(variables.id) });
      queryClient.invalidateQueries({ queryKey: marketingKeys.templates() });
      toast.success('Template updated successfully');
    },
    onError: () => {
      toast.error('Failed to update template');
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => marketingApi.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.templates() });
      toast.success('Template deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete template');
    },
  });
}

// Campaigns Queries
export function useCampaigns(params = {}) {
  return useQuery({
    queryKey: [...marketingKeys.campaigns(), params],
    queryFn: async () => {
      const res = await marketingApi.getCampaigns(params);
      return res.data.campaigns || res.data || [];
    },
  });
}

export function useCampaign(id, options = {}) {
  return useQuery({
    queryKey: marketingKeys.campaign(id),
    queryFn: async () => {
      const res = await marketingApi.getCampaign(id);
      return res.data;
    },
    enabled: !!id,
    ...options,
  });
}

export function useCampaignRecipients(id, params = {}) {
  return useQuery({
    queryKey: [...marketingKeys.campaignRecipients(id), params],
    queryFn: async () => {
      const res = await marketingApi.getCampaignRecipients(id, params);
      return res.data.recipients || res.data || [];
    },
    enabled: !!id,
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => marketingApi.createCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.campaigns() });
      toast.success('Campaign created successfully');
    },
    onError: () => {
      toast.error('Failed to create campaign');
    },
  });
}

export function useStartCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, segment_id, current_status, total_recipients }) => {
      // Only load recipients if campaign is in draft and has no recipients loaded yet
      if (current_status === 'draft' && (total_recipients || 0) === 0 && segment_id) {
        await marketingApi.loadFromSegment(id, { segment_id });
      }
      // Then start
      return marketingApi.startCampaign(id);
    },
    onMutate: async ({ id }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: marketingKeys.campaign(id) });
      
      // Snapshot previous value
      const previousCampaign = queryClient.getQueryData(marketingKeys.campaign(id));
      
      // Optimistically update
      queryClient.setQueryData(marketingKeys.campaign(id), (old) => ({
        ...old,
        status: 'running',
      }));
      
      return { previousCampaign };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      queryClient.setQueryData(marketingKeys.campaign(variables.id), context.previousCampaign);
      toast.error('Failed to start campaign');
    },
    onSettled: (data, error, variables) => {
      // Refetch to sync with server
      queryClient.invalidateQueries({ queryKey: marketingKeys.campaign(variables.id) });
      queryClient.invalidateQueries({ queryKey: marketingKeys.campaigns() });
    },
    onSuccess: () => {
      toast.success('Campaign started');
    },
  });
}

export function usePauseCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => marketingApi.pauseCampaign(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: marketingKeys.campaign(id) });
      const previousCampaign = queryClient.getQueryData(marketingKeys.campaign(id));
      
      queryClient.setQueryData(marketingKeys.campaign(id), (old) => ({
        ...old,
        status: 'paused',
      }));
      
      return { previousCampaign };
    },
    onError: (err, id, context) => {
      queryClient.setQueryData(marketingKeys.campaign(id), context.previousCampaign);
      toast.error('Failed to pause campaign');
    },
    onSettled: (data, error, id) => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.campaign(id) });
      queryClient.invalidateQueries({ queryKey: marketingKeys.campaigns() });
    },
    onSuccess: () => {
      toast.success('Campaign paused');
    },
  });
}

export function useResumeCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => marketingApi.resumeCampaign(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: marketingKeys.campaign(id) });
      const previousCampaign = queryClient.getQueryData(marketingKeys.campaign(id));
      
      queryClient.setQueryData(marketingKeys.campaign(id), (old) => ({
        ...old,
        status: 'running',
      }));
      
      return { previousCampaign };
    },
    onError: (err, id, context) => {
      queryClient.setQueryData(marketingKeys.campaign(id), context.previousCampaign);
      toast.error('Failed to resume campaign');
    },
    onSettled: (data, error, id) => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.campaign(id) });
      queryClient.invalidateQueries({ queryKey: marketingKeys.campaigns() });
    },
    onSuccess: () => {
      toast.success('Campaign resumed');
    },
  });
}

// Segments Queries
export function useSegments() {
  return useQuery({
    queryKey: marketingKeys.segments(),
    queryFn: async () => {
      const res = await marketingApi.getSegments();
      return res.data.segments || res.data || [];
    },
  });
}
